/**
 * FacilityDrawer — right-side sheet showing a single facility (iter 170 split).
 *
 * Owns all fetch + mutation axios calls for its facility, then hands the
 * pure state down to the three tabs (Contacts / Notes / Tasks). Parents
 * only need to pass ``facilityId`` + close/change callbacks; there's no
 * shared parent state to sync.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Building2, Phone, Users, StickyNote, CheckSquare, Percent, Timer } from 'lucide-react';
import { Button } from '../ui/button';
import { MiniStat } from './uiKit';
import { API, fmtPct, fmtHours } from './formatters';
import { ContactsTab } from './ContactsTab';
import { NotesTab } from './NotesTab';
import { TasksTab } from './TasksTab';

export function FacilityDrawer({ facilityId, onClose, onChanged, getAuthHeader }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('contacts');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/crm/facilities/${facilityId}`, getAuthHeader());
      setDetail(data);
    } catch (e) {
      toast.error('Could not load facility');
    } finally {
      setLoading(false);
    }
  }, [facilityId, getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  // ─── Mutations — kept as tiny wrappers so the tabs stay presentational ───
  const runMutation = async (fn) => {
    setBusy(true);
    try {
      await fn();
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  const addContact  = (payload)      => runMutation(async () => {
    await axios.post(`${API}/crm/facilities/${facilityId}/contacts`, payload, getAuthHeader());
    toast.success('Contact added');
  });
  const deleteContact = (cid)        => runMutation(async () => {
    await axios.delete(`${API}/crm/facilities/${facilityId}/contacts/${cid}`, getAuthHeader());
    toast.success('Contact removed');
  });
  const addNote     = (text)         => runMutation(async () => {
    if (!text.trim()) return;
    await axios.post(`${API}/crm/facilities/${facilityId}/notes`, { text: text.trim() }, getAuthHeader());
    toast.success('Note added');
  });
  const addTask     = (title, dueAt) => runMutation(async () => {
    if (!title.trim()) return;
    await axios.post(`${API}/crm/facilities/${facilityId}/tasks`,
      { title: title.trim(), due_at: dueAt || null },
      getAuthHeader());
    toast.success('Task added');
  });
  const patchTask   = (tid, patch)   => runMutation(async () => {
    await axios.patch(`${API}/crm/facilities/${facilityId}/tasks/${tid}`, patch, getAuthHeader());
  });

  if (loading || !detail) {
    return (
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[70] flex items-center justify-center"
        onClick={onClose}
        data-testid="crm-drawer-loading"
      >
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const s = detail.volume_snapshot || {};
  const tabs = [
    ['contacts', 'Contacts', Users],
    ['notes',    'Notes',    StickyNote],
    ['tasks',    'Tasks',    CheckSquare],
  ];

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[70] flex justify-end"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="crm-drawer"
    >
      <div className="bg-slate-950 border-l border-slate-800 w-full max-w-3xl h-full overflow-y-auto shadow-2xl">
        <header className="sticky top-0 bg-slate-950/95 backdrop-blur border-b border-slate-800 p-5 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-slate-100 flex items-center gap-2">
                <Building2 className="h-5 w-5 text-slate-500" />
                {detail.name}
              </h2>
              <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-400">
                {detail.phone && (
                  <span className="inline-flex items-center gap-1">
                    <Phone className="h-3 w-3" />{detail.phone}
                  </span>
                )}
                {detail.address && <span>{detail.address}</span>}
                {detail.npi && <span>NPI {detail.npi}</span>}
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="crm-drawer-close">
              Close
            </Button>
          </div>

          <div className="mt-4 grid grid-cols-4 gap-2 text-center">
            <MiniStat label="Volume"        value={s.total_referrals ?? 0} />
            <MiniStat label="Accepted"      value={s.accepted ?? 0} tone="text-emerald-300" />
            <MiniStat label="Conversion"    value={fmtPct(s.conversion_rate)}          icon={Percent} />
            <MiniStat label="Time to admit" value={fmtHours(s.avg_time_to_admit_hours)} icon={Timer} />
          </div>

          <div className="mt-4 border-b border-slate-800 flex gap-1">
            {tabs.map(([id, label, Icon]) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px flex items-center gap-1.5 ${
                  tab === id
                    ? 'border-cn-primary text-cn-primary'
                    : 'border-transparent text-slate-500 hover:text-slate-300'
                }`}
                data-testid={`crm-tab-${id}`}
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>
        </header>

        <div className="p-5 space-y-4">
          {tab === 'contacts' && (
            <ContactsTab
              contacts={detail.contacts || []}
              onAdd={addContact}
              onDelete={deleteContact}
              busy={busy}
            />
          )}
          {tab === 'notes' && (
            <NotesTab notes={detail.notes || []} onAdd={addNote} busy={busy} />
          )}
          {tab === 'tasks' && (
            <TasksTab
              tasks={detail.tasks || []}
              onAdd={addTask}
              onPatch={patchTask}
              busy={busy}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default FacilityDrawer;
